import React from 'react';
import {Platform, StyleSheet, Text, View} from 'react-native';

import {lineHeight, resolveWeight, space, type} from '../../design-system/tokens';
import theme from '../../style/theme';

import {Band} from './breakpoints';
import {headerChrome} from './headerChrome';

export const NON_ECOM_TITLE = 'Online shopping is not available at this store';
export const NON_ECOM_BODY =
  'Promotions and catalogues are available for this store. To buy online, select a store that offers online shopping.';

/** The body words that link to the store selector when `onSelectStore` is passed. */
export const NON_ECOM_LINK_TEXT = 'select a store';
const [BODY_BEFORE_LINK, BODY_AFTER_LINK] = NON_ECOM_BODY.split(NON_ECOM_LINK_TEXT);

// SHORTCUT: #0054a6 is the redline span colour (non-ecom-header spec.json, Figma 475:24520);
// no link colour token exists and the brand themes have no link colour. Ceiling: every brand
// draws the link in Bottlemart blue. Upgrade path: a DIS-1735 link colour token.
const LINK_COLOUR = '#0054a6';

/**
 * Web only: RNW `Text` attaches `onClick` alone, and a `span[role=link]` ignores Enter, so the
 * link would be focusable but not operable from the keyboard (WCAG 2.1.1). Enter calls the
 * handler; Enter on a span fires no click, so the handler runs once. Native ignores the prop.
 */
const linkKeyProps = (onSelectStore: () => void) =>
  Platform.OS === 'web'
    ? {
        onKeyDown: (event: {key?: string; preventDefault?: () => void}) => {
          if (event.key === 'Enter') {
            event.preventDefault?.();
            onSelectStore();
          }
        },
      }
    : {};

export type NonEcomNoticeProps = {
  band: Band;
  /** When passed, "select a store" in the body is a link that calls it (ruling R29). */
  onSelectStore?: () => void;
};

/**
 * The notice under the header when the selected store does not sell online (DIS-1840,
 * Figma 475:24520 desktop, 475:24536 mobile). SiteHeader renders it and hides it when compact.
 */
export const NonEcomNotice = ({band, onSelectStore}: NonEcomNoticeProps) => {
  const mobile = band === 'mobile';
  return (
    <View
      testID="site-header-non-ecom-notice"
      style={[styles.block, mobile ? styles.blockMobile : styles.blockDesktop]}>
      <Text
        accessibilityRole="header"
        style={[styles.title, mobile ? styles.titleMobile : styles.titleDesktop]}>
        {NON_ECOM_TITLE}
      </Text>
      <Text style={[styles.body, mobile ? styles.bodyMobile : styles.bodyDesktop]}>
        {onSelectStore ? (
          <>
            {BODY_BEFORE_LINK}
            <Text
              testID="site-header-non-ecom-select-store"
              accessibilityRole="link"
              onPress={onSelectStore}
              {...linkKeyProps(onSelectStore)}
              style={styles.link}>
              {NON_ECOM_LINK_TEXT}
            </Text>
            {BODY_AFTER_LINK}
          </>
        ) : (
          NON_ECOM_BODY
        )}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  block: {alignItems: 'center', padding: space.s24},
  // Figma homecontent 474:21829: the content column has padding-top 46 below the header and a
  // 40 gap between its sections; the notice is its first section. The page content starts at
  // the header's bottom edge, so the notice carries both: 46 above, 40 below. Mobile keeps 24
  // (no mobile frame measured).
  // SHORTCUT: 46 is off the spacing scale; no token. Upgrade path: DIS-1735 adds a page-top
  // offset token, or design moves it onto the scale.
  blockDesktop: {gap: space.s16, paddingTop: 46, paddingBottom: space.s40},
  blockMobile: {gap: space.s8},
  title: {color: headerChrome.accent, textAlign: 'center', textTransform: 'uppercase'},
  // SHORTCUT: text widths 583 / 466 have no layout token (Figma 475:24520). 466 sets the
  // desktop body line breaks. 583 only caps the title width: measured 2026-10-20 with
  // HarfBuzz (kerned, Bold 20), the current title is 518.0 px in Rubik and 530.7 px in
  // Geologica, so it does not wrap. Ceiling: other copy wraps at Figma's widths, not its
  // own. Upgrade path: a DIS-1735 layout token for narrow centred text, or drop the caps.
  titleDesktop: {...type.heading.h1, ...lineHeight.heading.h1, maxWidth: 583},
  // SHORTCUT: Figma mobile draws 13.89 Bold / 13.75 Regular, desktop 20 / 18 scaled by
  // 0.694 / 0.764 (off-scale artefact). Both take type.paragraph.p2 14; the title keeps
  // Figma's Bold through resolveWeight(). Upgrade path: design confirms 14, or DIS-1735 adds
  // a 14 Bold heading token.
  titleMobile: {
    ...type.paragraph.p2,
    ...resolveWeight('bold'),
    ...lineHeight.paragraph.p2,
  },
  body: {color: theme.colour.text.content.primary, textAlign: 'center'},
  // SHORTCUT: 466 is the Figma body width; ceiling and upgrade path as titleDesktop.
  bodyDesktop: {...type.paragraph.p1, ...lineHeight.paragraph.p1, maxWidth: 466},
  bodyMobile: {...type.paragraph.p2, ...lineHeight.paragraph.p2},
  // R34: underlined, so the link is not shown by colour alone (2.82:1 against the body text,
  // WCAG 1.4.1).
  link: {color: LINK_COLOUR, textDecorationLine: 'underline'},
});
